/**
 * Meta Pixel Consent Management
 * Handles Cookiebot consent checks for GDPR compliance
 */

import { isCookiebotMarketingConsentGiven } from "@/utils/analytics/cookiebot";

let isMetaPixelInitialized = false;
let initializationPromise: Promise<boolean> | null = null;

/**
 * Check if Meta Pixel is available
 */
export const isMetaPixelAvailable = (): boolean => {
  return Boolean(process.env.NEXT_PUBLIC_META_PIXEL_ID?.trim());
};

/**
 * Check if Cookiebot consent is given for marketing (required for advertising pixels)
 * Re-exported for convenience
 */
export const isMetaPixelConsentGiven = isCookiebotMarketingConsentGiven;

/**
 * Initialize Meta Pixel if consent is given
 */
export const initializeMetaPixel = async (): Promise<boolean> => {
  // If initialization is already in progress, await its completion
  if (initializationPromise) {
    return await initializationPromise;
  }

  const initPromise = (async (): Promise<boolean> => {
    if (isMetaPixelInitialized) {
      if (process.env.NODE_ENV === "development") {
        console.log("Meta Pixel already initialized");
      }
      return true;
    }

    if (!isMetaPixelConsentGiven()) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "Meta Pixel initialization skipped: No Cookiebot consent for marketing",
        );
      }
      return false;
    }

    if (!isMetaPixelAvailable()) {
      return false;
    }

    try {
      // Check if fbq is already loaded
      if (typeof window !== "undefined" && window.fbq) {
        isMetaPixelInitialized = true;
        if (process.env.NODE_ENV === "development") {
          console.log("Meta Pixel already initialized");
        }
        return true;
      }

      const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID?.trim();

      if (!pixelId) {
        return false;
      }

      // Meta Pixel initialization code
      (function (f: any, b: any, e: string, v: string, n: any, t: any, s: any) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod
            ? n.callMethod.apply(n, arguments)
            : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js",
        null,
        null,
        null,
      );

      window.fbq?.("init", pixelId);
      window.fbq?.("track", "PageView");

      isMetaPixelInitialized = true;
      if (process.env.NODE_ENV === "development") {
        console.log(`Meta Pixel initialized successfully with ID: ${pixelId}`);
      }
      return true;
    } catch (error) {
      console.error("Failed to initialize Meta Pixel:", error);
      return false;
    }
  })();

  initializationPromise = initPromise;

  const result = await initPromise;

  // If initialization failed, reset the promise to allow retries
  if (!result) {
    initializationPromise = null;
  }

  return result;
};

/**
 * Reset Meta Pixel state (useful when consent is withdrawn)
 */
export const resetMetaPixelState = (): void => {
  isMetaPixelInitialized = false;
  initializationPromise = null;
};

/**
 * Check if Meta Pixel is initialized
 */
export const isMetaPixelReady = (): boolean => {
  return (
    isMetaPixelInitialized &&
    typeof window !== "undefined" &&
    !!window.fbq
  );
};

// Setup Cookiebot event listeners for consent changes
let listenersSetup = false;

export const setupMetaPixelCookiebotListeners = (): (() => void) => {
  if (typeof window === "undefined") {
    return () => {};
  }

  if (listenersSetup) {
    if (process.env.NODE_ENV === "development") {
      console.debug("Meta Pixel Cookiebot listeners already set up");
    }
    return () => {};
  }

  const handleAccept = async () => {
    if (isMetaPixelConsentGiven()) {
      await initializeMetaPixel();
    }
  };

  const handleDecline = () => {
    resetMetaPixelState();
    if (process.env.NODE_ENV === "development") {
      console.log("Meta Pixel tracking disabled due to consent withdrawal");
    }
  };

  const handleConsentChanged = async () => {
    if (isMetaPixelConsentGiven() && !isMetaPixelInitialized) {
      await initializeMetaPixel();
    } else if (!isMetaPixelConsentGiven() && isMetaPixelInitialized) {
      resetMetaPixelState();
    }
  };

  window.addEventListener("CookiebotOnAccept", handleAccept);
  window.addEventListener("CookiebotOnDecline", handleDecline);
  window.addEventListener("CookiebotOnConsentChanged", handleConsentChanged);

  listenersSetup = true;

  // Return a cleanup function
  return () => {
    window.removeEventListener("CookiebotOnAccept", handleAccept);
    window.removeEventListener("CookiebotOnDecline", handleDecline);
    window.removeEventListener(
      "CookiebotOnConsentChanged",
      handleConsentChanged,
    );
    listenersSetup = false;
    if (process.env.NODE_ENV === "development") {
      console.log("Meta Pixel Cookiebot listeners cleaned up.");
    }
  };
};
